import { useState, useEffect } from "react";
import { useAdminBoard } from "@/contexts/AdminBoardContext";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  Bot,
  Users,
  Briefcase,
  Phone,
  Mic,
  Globe,
  Plus,
  Loader2,
  Search,
  Play,
  Pause,
  Settings,
  Eye,
  Zap,
  HeadphonesIcon,
  Shield,
  BarChart3,
  FileText,
  Bell,
  Building,
  AlertTriangle,
  Clock
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";

const API_URL = process.env.REACT_APP_API_URL;

const FRONT_OFFICE_TYPES = [
  { value: "receptionist", label: "AI Receptionist", icon: HeadphonesIcon, description: "24/7 call answering & routing" },
  { value: "sales", label: "AI Sales Agent", icon: Briefcase, description: "Lead qualification & booking" },
  { value: "scheduler", label: "AI Scheduler", icon: Clock, description: "Appointment management" },
  { value: "support", label: "AI Support", icon: Users, description: "Customer service & troubleshooting" },
  { value: "billing", label: "AI Billing", icon: FileText, description: "Payment & account inquiries" },
  { value: "insurance_intake", label: "AI Insurance", icon: Shield, description: "RIBO/FSRA compliant intake" },
  { value: "emergency", label: "AI Emergency", icon: AlertTriangle, description: "After-hours urgent calls" },
  { value: "renewal", label: "AI Renewal", icon: Zap, description: "Contract renewals & retention" },
  { value: "survey", label: "AI Survey", icon: BarChart3, description: "NPS, CSAT, feedback collection" },
  { value: "collections", label: "AI Collections", icon: FileText, description: "Respectful payment recovery" },
  { value: "upsell", label: "AI Upsell", icon: Zap, description: "Cross-sell & upsell opportunities" }
];

const BACK_OFFICE_TYPES = [
  { value: "leadiq", label: "LeadIQ", icon: Zap, description: "Lead qualification & scoring" },
  { value: "callguard", label: "CallGuard", icon: Shield, description: "QA & call scoring" },
  { value: "policywatch", label: "PolicyWatch", icon: Shield, description: "Compliance monitoring" },
  { value: "revenuesync", label: "RevenueSync", icon: BarChart3, description: "Billing reconciliation" },
  { value: "crmsync", label: "CRM Sync", icon: Users, description: "CRM integration" },
  { value: "notification", label: "Notification", icon: Bell, description: "Multi-channel dispatch" },
  { value: "franchiseops", label: "FranchiseOps", icon: Building, description: "Territory oversight" },
  { value: "risk_escalation", label: "Risk & Escalation", icon: AlertTriangle, description: "Risk management" },
  { value: "analytics", label: "Analytics", icon: BarChart3, description: "Insights generation" }
];

const LANGUAGES = [
  { code: "en", name: "English", regions: ["na", "eu"] },
  { code: "es", name: "Spanish", regions: ["latam", "eu"] },
  { code: "fr", name: "French", regions: ["eu"] },
  { code: "ar", name: "Arabic", regions: ["mena"] },
  { code: "hi", name: "Hindi", regions: ["south_asia"] },
  { code: "ur", name: "Urdu", regions: ["south_asia"] },
  { code: "de", name: "German", regions: ["eu"] },
  { code: "ru", name: "Russian", regions: ["eu"] }
];

const CULTURAL_TONES = [
  { value: "neutral", label: "Neutral", description: "Professional, standard tone" },
  { value: "local", label: "Local", description: "Regional expressions & idioms" },
  { value: "formal", label: "Formal", description: "Highly professional, respectful" },
  { value: "friendly", label: "Friendly", description: "Warm, approachable tone" }
];

export default function AdminEnterpriseAgents() {
  const { user } = useAdminBoard();
  const [loading, setLoading] = useState(true);
  const [agents, setAgents] = useState([]);
  const [frontOfficeAgents, setFrontOfficeAgents] = useState([]);
  const [backOfficeAgents, setBackOfficeAgents] = useState([]);
  const [activeTab, setActiveTab] = useState("front_office");
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [creating, setCreating] = useState(false);
  const [nameSuggestions, setNameSuggestions] = useState([]);
  
  const [newAgent, setNewAgent] = useState({
    business_id: "platform",
    agent_class: "front_office",
    agent_type: "",
    name: "",
    language: "en",
    region: "na",
    cultural_tone: "neutral",
    system_prompt: "",
    personality: "",
    role: ""
  });

  useEffect(() => {
    loadAgents();
  }, []);

  useEffect(() => {
    if (newAgent.language && newAgent.region && newAgent.agent_class === "front_office") {
      loadNameSuggestions();
    }
  }, [newAgent.language, newAgent.region, newAgent.agent_class]);

  const loadAgents = async () => {
    setLoading(true);
    try {
      const [frontRes, backRes] = await Promise.all([
        fetch(`${API_URL}/api/v2/agents/front-office?business_id=platform`),
        fetch(`${API_URL}/api/v2/agents/back-office?business_id=platform`)
      ]);
      
      if (frontRes.ok) {
        const data = await frontRes.json();
        setFrontOfficeAgents(data);
      }
      if (backRes.ok) {
        const data = await backRes.json();
        setBackOfficeAgents(data);
      }
    } catch (error) {
      console.error("Failed to load agents:", error);
      toast.error("Failed to load agents");
    } finally {
      setLoading(false);
    }
  };

  const loadNameSuggestions = async () => {
    try {
      const response = await fetch(
        `${API_URL}/api/v2/agents/names/suggest?language=${newAgent.language}&region=${newAgent.region}`
      );
      if (response.ok) {
        const data = await response.json();
        setNameSuggestions(data.suggestions || []);
      }
    } catch (error) {
      console.error("Failed to load name suggestions:", error);
    }
  };

  const createAgent = async () => {
    if (!newAgent.agent_type) {
      toast.error("Please select an agent type");
      return;
    }
    
    if (newAgent.agent_class === "front_office" && !newAgent.name) {
      toast.error("Please enter or select a name for the agent");
      return;
    }
    
    setCreating(true);
    try {
      // For back-office agents, use functional name
      const agentData = {
        ...newAgent,
        name: newAgent.agent_class === "back_office" 
          ? BACK_OFFICE_TYPES.find(t => t.value === newAgent.agent_type)?.label || newAgent.agent_type
          : newAgent.name,
        role: newAgent.agent_class === "front_office" 
          ? FRONT_OFFICE_TYPES.find(t => t.value === newAgent.agent_type)?.label || ""
          : BACK_OFFICE_TYPES.find(t => t.value === newAgent.agent_type)?.label || ""
      };
      
      const response = await fetch(`${API_URL}/api/v2/agents`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(agentData)
      });
      
      if (!response.ok) throw new Error("Failed to create agent");
      
      const created = await response.json();
      toast.success(`Agent "${created.name}" created successfully`);
      setShowCreateDialog(false);
      resetNewAgent();
      loadAgents();
    } catch (error) {
      toast.error("Failed to create agent");
    } finally {
      setCreating(false);
    }
  };

  const toggleAgentPause = async (agent) => {
    try {
      const endpoint = agent.is_paused 
        ? `${API_URL}/api/v2/agents/${agent.id}/resume`
        : `${API_URL}/api/v2/agents/${agent.id}/pause`;
      
      const response = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ reason: "Manual toggle" })
      });
      
      if (!response.ok) throw new Error("Failed to toggle agent");
      
      toast.success(agent.is_paused ? "Agent resumed" : "Agent paused");
      loadAgents();
    } catch (error) {
      toast.error("Failed to toggle agent status");
    }
  };

  const resetNewAgent = () => {
    setNewAgent({
      business_id: "platform",
      agent_class: "front_office",
      agent_type: "",
      name: "",
      language: "en",
      region: "na",
      cultural_tone: "neutral",
      system_prompt: "",
      personality: "",
      role: ""
    });
    setNameSuggestions([]);
  };

  const getAgentTypeInfo = (type, isFrontOffice) => {
    const types = isFrontOffice ? FRONT_OFFICE_TYPES : BACK_OFFICE_TYPES;
    return types.find(t => t.value === type) || { label: type, icon: Bot };
  };

  const renderAgentCard = (agent, isFrontOffice) => {
    const typeInfo = getAgentTypeInfo(agent.agent_type, isFrontOffice);
    const Icon = typeInfo.icon || Bot;
    
    return (
      <Card 
        key={agent.id} 
        className={`relative overflow-hidden transition-all ${
          agent.is_paused ? "opacity-60" : ""
        }`}
      >
        {/* Status indicator */}
        <div className={`absolute top-0 left-0 w-1 h-full ${
          agent.is_paused ? "bg-amber-500" : agent.is_active ? "bg-emerald-500" : "bg-slate-400"
        }`} />
        
        <CardContent className="pt-6">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-3">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                isFrontOffice 
                  ? "bg-gradient-to-br from-cyan-500/20 to-blue-500/20 text-cyan-600"
                  : "bg-gradient-to-br from-purple-500/20 to-pink-500/20 text-purple-600"
              }`}>
                <Icon className="w-6 h-6" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="font-semibold">{agent.name}</h3>
                  {isFrontOffice && (
                    <Badge variant="outline" className="text-xs">
                      <Globe className="w-3 h-3 mr-1" />
                      {agent.language?.toUpperCase()}
                    </Badge>
                  )}
                </div>
                <p className="text-sm text-muted-foreground">{typeInfo.label}</p>
                <p className="text-xs text-muted-foreground mt-1">{typeInfo.description}</p>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => toggleAgentPause(agent)}
                className={agent.is_paused ? "text-emerald-600" : "text-amber-600"}
              >
                {agent.is_paused ? (
                  <Play className="w-4 h-4" />
                ) : (
                  <Pause className="w-4 h-4" />
                )}
              </Button>
              <Button variant="ghost" size="icon">
                <Settings className="w-4 h-4" />
              </Button>
            </div>
          </div>
          
          {/* Agent metadata */}
          <div className="mt-4 flex items-center gap-4 text-xs text-muted-foreground">
            <span className="flex items-center gap-1">
              <Phone className="w-3 h-3" />
              {agent.total_calls || 0} calls
            </span>
            {isFrontOffice && agent.cultural_tone && (
              <span className="flex items-center gap-1">
                <Mic className="w-3 h-3" />
                {agent.cultural_tone}
              </span>
            )}
            {agent.is_paused && (
              <Badge variant="outline" className="text-amber-600 border-amber-300">
                Paused
              </Badge>
            )}
          </div>
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6" data-testid="enterprise-agents-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Agent Registry</h1>
          <p className="text-muted-foreground">
            Manage front-office and back-office AI agents
          </p>
        </div>
        <Button onClick={() => setShowCreateDialog(true)} data-testid="create-agent-btn">
          <Plus className="w-4 h-4 mr-2" />
          Create Agent
        </Button>
      </div>

      {/* Stats */}
      <div className="grid md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Front-Office</p>
                <p className="text-2xl font-bold">{frontOfficeAgents.length}</p>
              </div>
              <div className="w-10 h-10 rounded-lg bg-cyan-100 text-cyan-600 flex items-center justify-center">
                <HeadphonesIcon className="w-5 h-5" />
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-2">Customer-facing agents</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Back-Office</p>
                <p className="text-2xl font-bold">{backOfficeAgents.length}</p>
              </div>
              <div className="w-10 h-10 rounded-lg bg-purple-100 text-purple-600 flex items-center justify-center">
                <Briefcase className="w-5 h-5" />
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-2">Internal automation</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Active</p>
                <p className="text-2xl font-bold text-emerald-600">
                  {[...frontOfficeAgents, ...backOfficeAgents].filter(a => !a.is_paused).length}
                </p>
              </div>
              <div className="w-10 h-10 rounded-lg bg-emerald-100 text-emerald-600 flex items-center justify-center">
                <Zap className="w-5 h-5" />
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-2">Currently running</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Languages</p>
                <p className="text-2xl font-bold">
                  {new Set(frontOfficeAgents.map(a => a.language)).size}
                </p>
              </div>
              <div className="w-10 h-10 rounded-lg bg-blue-100 text-blue-600 flex items-center justify-center">
                <Globe className="w-5 h-5" />
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-2">Supported</p>
          </CardContent>
        </Card>
      </div>

      {/* Agent Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="front_office" className="flex items-center gap-2">
            <HeadphonesIcon className="w-4 h-4" />
            Front-Office ({frontOfficeAgents.length})
          </TabsTrigger>
          <TabsTrigger value="back_office" className="flex items-center gap-2">
            <Briefcase className="w-4 h-4" />
            Back-Office ({backOfficeAgents.length})
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="front_office" className="mt-6">
          {frontOfficeAgents.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <HeadphonesIcon className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                <h3 className="text-lg font-semibold mb-2">No Front-Office Agents</h3>
                <p className="text-muted-foreground mb-4">
                  Create customer-facing agents like receptionists, sales agents, and support
                </p>
                <Button onClick={() => {
                  setNewAgent({ ...newAgent, agent_class: "front_office" });
                  setShowCreateDialog(true);
                }}>
                  <Plus className="w-4 h-4 mr-2" />
                  Create Front-Office Agent
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {frontOfficeAgents.map((agent) => renderAgentCard(agent, true))}
            </div>
          )}
        </TabsContent>
        
        <TabsContent value="back_office" className="mt-6">
          {backOfficeAgents.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Briefcase className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                <h3 className="text-lg font-semibold mb-2">No Back-Office Agents</h3>
                <p className="text-muted-foreground mb-4">
                  Create internal agents for QA, compliance, analytics, and automation
                </p>
                <Button onClick={() => {
                  setNewAgent({ ...newAgent, agent_class: "back_office" });
                  setShowCreateDialog(true);
                }}>
                  <Plus className="w-4 h-4 mr-2" />
                  Create Back-Office Agent
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {backOfficeAgents.map((agent) => renderAgentCard(agent, false))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Create Agent Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={(open) => {
        setShowCreateDialog(open);
        if (!open) resetNewAgent();
      }}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bot className="w-5 h-5" />
              Create New Agent
            </DialogTitle>
            <DialogDescription>
              Configure a new AI agent for your organization
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Agent Class Selection */}
            <div className="space-y-2">
              <Label>Agent Type</Label>
              <div className="grid grid-cols-2 gap-4">
                <button
                  type="button"
                  onClick={() => setNewAgent({ ...newAgent, agent_class: "front_office", agent_type: "" })}
                  className={`p-4 rounded-lg border-2 transition-all text-left ${
                    newAgent.agent_class === "front_office"
                      ? "border-cyan-500 bg-cyan-50 dark:bg-cyan-950"
                      : "border-muted hover:border-cyan-300"
                  }`}
                >
                  <HeadphonesIcon className={`w-6 h-6 mb-2 ${
                    newAgent.agent_class === "front_office" ? "text-cyan-600" : "text-muted-foreground"
                  }`} />
                  <p className="font-semibold">Front-Office</p>
                  <p className="text-xs text-muted-foreground">Customer-facing with human name</p>
                </button>
                <button
                  type="button"
                  onClick={() => setNewAgent({ ...newAgent, agent_class: "back_office", agent_type: "", name: "" })}
                  className={`p-4 rounded-lg border-2 transition-all text-left ${
                    newAgent.agent_class === "back_office"
                      ? "border-purple-500 bg-purple-50 dark:bg-purple-950"
                      : "border-muted hover:border-purple-300"
                  }`}
                >
                  <Briefcase className={`w-6 h-6 mb-2 ${
                    newAgent.agent_class === "back_office" ? "text-purple-600" : "text-muted-foreground"
                  }`} />
                  <p className="font-semibold">Back-Office</p>
                  <p className="text-xs text-muted-foreground">Internal automation, no customer contact</p>
                </button>
              </div>
            </div>
            
            {/* Role Selection */}
            <div className="space-y-2">
              <Label>Agent Role *</Label>
              <Select
                value={newAgent.agent_type}
                onValueChange={(value) => setNewAgent({ ...newAgent, agent_type: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a role" />
                </SelectTrigger>
                <SelectContent>
                  {(newAgent.agent_class === "front_office" ? FRONT_OFFICE_TYPES : BACK_OFFICE_TYPES).map((type) => {
                    const Icon = type.icon;
                    return (
                      <SelectItem key={type.value} value={type.value}>
                        <div className="flex items-center gap-2">
                          <Icon className="w-4 h-4" />
                          <span>{type.label}</span>
                          <span className="text-xs text-muted-foreground">- {type.description}</span>
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
            
            {/* Front-Office specific fields */}
            {newAgent.agent_class === "front_office" && (
              <>
                {/* Language & Region */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Language</Label>
                    <Select
                      value={newAgent.language}
                      onValueChange={(value) => {
                        const lang = LANGUAGES.find(l => l.code === value);
                        setNewAgent({ 
                          ...newAgent, 
                          language: value,
                          region: lang?.regions[0] || "na"
                        });
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {LANGUAGES.map((lang) => (
                          <SelectItem key={lang.code} value={lang.code}>
                            {lang.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Cultural Tone</Label>
                    <Select
                      value={newAgent.cultural_tone}
                      onValueChange={(value) => setNewAgent({ ...newAgent, cultural_tone: value })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {CULTURAL_TONES.map((tone) => (
                          <SelectItem key={tone.value} value={tone.value}>
                            <div>
                              <span>{tone.label}</span>
                              <span className="text-xs text-muted-foreground ml-2">- {tone.description}</span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                {/* Name Selection */}
                <div className="space-y-2">
                  <Label>Agent Name *</Label>
                  <Input
                    value={newAgent.name}
                    onChange={(e) => setNewAgent({ ...newAgent, name: e.target.value })}
                    placeholder="Enter name or select from suggestions"
                  />
                  {nameSuggestions.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      <span className="text-xs text-muted-foreground">Suggestions:</span>
                      {nameSuggestions.map((suggestion, i) => (
                        <Badge
                          key={i}
                          variant="outline"
                          className="cursor-pointer hover:bg-primary hover:text-primary-foreground"
                          onClick={() => setNewAgent({ ...newAgent, name: suggestion.name })}
                        >
                          {suggestion.name}
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
                
                {/* Personality */}
                <div className="space-y-2">
                  <Label>Personality (Optional)</Label>
                  <Input
                    value={newAgent.personality}
                    onChange={(e) => setNewAgent({ ...newAgent, personality: e.target.value })}
                    placeholder="e.g., Warm, professional, patient"
                  />
                </div>
              </>
            )}
            
            {/* System Prompt */}
            <div className="space-y-2">
              <Label>Custom Instructions (Optional)</Label>
              <Textarea
                value={newAgent.system_prompt}
                onChange={(e) => setNewAgent({ ...newAgent, system_prompt: e.target.value })}
                placeholder="Add any custom instructions for this agent..."
                rows={3}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
              Cancel
            </Button>
            <Button onClick={createAgent} disabled={creating}>
              {creating ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4 mr-2" />
                  Create Agent
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
